import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authClient } from "@/lib/auth-client";

const usernamePattern = /^[a-z0-9][a-z0-9_-]{2,23}$/;

export const SignUpPage = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedUsername = username.trim().toLowerCase();
    if (!usernamePattern.test(normalizedUsername)) {
      setError("Use 3–24 lowercase letters, numbers, hyphens, or underscores.");
      return;
    }

    setLoading(true);
    setError(null);
    const result = await authClient.signUp.email({
      name: name.trim(),
      username: normalizedUsername,
      email,
      password,
      callbackURL: "/projects",
    });
    setLoading(false);

    if (result.error) {
      setError(result.error.message ?? "Account creation failed.");
      return;
    }

    navigate("/projects", { replace: true });
  };

  return (
    <section className="grid min-h-[calc(100dvh-4rem)] place-items-center bg-white px-4 py-12 sm:px-6">
      <div className="w-full max-w-xs">
        <div className="pb-6">
          <p className="font-mono text-sm font-medium tracking-wide text-primary-strong">WAW BOARD</p>
          <h1 className="text-balance pt-2 text-3xl font-semibold tracking-tight text-neutral-950">
            Create your account
          </h1>
          <p className="text-pretty pt-2 text-base text-neutral-600 sm:text-sm">
            Choose a username your teammates can find.
          </p>
        </div>

        <form className="flex flex-col gap-4" onSubmit={onSubmit}>
          {error ? <Alert className="border-0 bg-red-50 text-red-800 ring-1 ring-red-900/10">{error}</Alert> : null}
          <div className="flex flex-col gap-2">
            <Label htmlFor="name">Full name</Label>
            <Input
              autoComplete="name"
              id="name"
              maxLength={80}
              name="name"
              onChange={(event) => setName(event.target.value)}
              required
              value={name}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="username">Username</Label>
            <Input
              aria-describedby="username-help"
              autoCapitalize="none"
              autoComplete="username"
              id="username"
              maxLength={24}
              minLength={3}
              name="username"
              onChange={(event) => setUsername(event.target.value.toLowerCase())}
              pattern="[a-z0-9][a-z0-9_-]{2,23}"
              required
              value={username}
            />
            <p className="text-base text-neutral-500 sm:text-sm" id="username-help">
              Lowercase letters, numbers, hyphens, and underscores.
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="email">Email</Label>
            <Input
              autoComplete="email"
              id="email"
              inputMode="email"
              name="email"
              onChange={(event) => setEmail(event.target.value)}
              required
              type="email"
              value={email}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="password">Password</Label>
            <Input
              autoComplete="new-password"
              id="password"
              minLength={8}
              name="password"
              onChange={(event) => setPassword(event.target.value)}
              required
              type="password"
              value={password}
            />
          </div>
          <Button className="w-full" disabled={loading} type="submit">
            {loading ? "Creating account" : "Create account"}
          </Button>
        </form>

        <p className="pt-5 text-center text-base text-neutral-600 sm:text-sm">
          Already have an account?{" "}
          <Link className="font-medium text-primary-strong underline-offset-4 hover:underline" to="/sign-in">
            Sign in
          </Link>
        </p>
      </div>
    </section>
  );
};
